import React from "react";
import { useLang } from "../contexts/LangContext";
import { getFlag } from "../lib/currencyFlags";

// Number formatter — tabular alignment via JetBrains Mono in CSS

const fmt = (n, d = 4) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });

const RatesTable = ({ rates = [], lastUpdate, bnrDate }) => {
  const { t, lang } = useLang();

  const formattedTs = lastUpdate
    ? new Date(lastUpdate).toLocaleString("ro-RO", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  return (
    <section id="rates" className="bg-paper pt-20 pb-10 lg:pt-28 lg:pb-14" data-testid="rates-section">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-overline text-coral mb-4">
              {t.rates.kicker}
            </div>
            <h2 className="font-display font-black tracking-tight text-4xl lg:text-5xl text-burgundy">
              {t.rates.title}
            </h2>
            <p className="mt-4 text-black/70 max-w-xl font-light leading-relaxed">
              {t.rates.subtitle}
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col items-start lg:items-end gap-3">
            <div className="text-[10px] uppercase tracking-overline text-black/50">
              {t.rates.last_update}
            </div>
            <div className="font-mono-num text-burgundy text-sm" data-testid="rates-last-update">
              {formattedTs}
            </div>
          </div>
        </div>

        <div
          className="border border-[#5a1a14]/15 bg-white overflow-hidden"
          data-testid="rates-table"
        >
          {/* Header */}
          <div className="hidden md:grid grid-cols-12 bg-burgundy text-white text-[10px] uppercase tracking-overline">
            <div className="col-span-5 px-6 py-4">{t.rates.currency}</div>
            <div className="col-span-2 px-6 py-4 text-right">{t.rates.buy}</div>
            <div className="col-span-2 px-6 py-4 text-right">{t.rates.sell}</div>
            <div className="col-span-3 px-6 py-4 text-right">{t.rates.bnr}</div>
          </div>

          {rates.map((r, idx) => {
            const name = (lang === "en" ? r.name_en : r.name_ro) || r.name_ro;
            const rowBg = idx % 2 === 0 ? "bg-white" : "bg-[#faf8f5]";
            return (
              <div key={r.code} data-testid={`rate-row-${r.code}`}>
                {/* ── Mobile row (< md) ── */}
                <div className={`md:hidden flex items-center justify-between px-4 py-3.5 border-t border-[#5a1a14]/10 ${rowBg}`}>
                  <div className="flex items-center gap-3">
                    {getFlag(r.code) && (
                      <img src={getFlag(r.code)} alt={r.code} className="w-7 h-auto rounded-sm shadow-sm shrink-0" />
                    )}
                    <div>
                      <div className="font-display font-bold text-burgundy text-base leading-none">{r.code}</div>
                      <div className="text-[11px] text-black/50 mt-0.5 leading-none">{name}</div>
                    </div>
                  </div>
                  <div className="text-right flex flex-col gap-1">
                    <div className="flex items-center justify-end gap-1.5">
                      <span className="text-[9px] uppercase tracking-overline text-black/35">{t.rates.buy}</span>
                      <span className="font-mono-num text-burgundy font-medium text-sm">{fmt(r.buy)}</span>
                    </div>
                    <div className="flex items-center justify-end gap-1.5">
                      <span className="text-[9px] uppercase tracking-overline text-black/35">{t.rates.sell}</span>
                      <span className="font-mono-num text-coral font-semibold text-sm">{fmt(r.sell)}</span>
                    </div>
                    {r.bnr_rate && (
                      <div className="flex items-center justify-end gap-1.5">
                        <span className="text-[9px] uppercase tracking-overline text-black/25">{t.rates.bnr}</span>
                        <span className="font-mono-num text-black/45 text-xs">{fmt(r.bnr_rate)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Desktop row (md+) ── */}
                <div className={`hidden md:grid md:grid-cols-12 items-center border-t border-[#5a1a14]/10 hover:bg-[#fff5f3] transition-colors ${rowBg}`}>
                  <div className="col-span-5 px-6 py-5 flex items-center gap-4">
                    <span className="font-mono-num text-coral text-xs w-8">{r.unit}</span>
                    {getFlag(r.code) && (
                      <img src={getFlag(r.code)} alt={r.code} className="w-7 h-auto rounded-sm shadow-sm" />
                    )}
                    <div className="flex flex-col">
                      <span className="font-display font-bold text-burgundy text-lg leading-none">{r.code}</span>
                      <span className="text-xs text-black/55 mt-1">{name}</span>
                    </div>
                  </div>
                  <div className="col-span-2 px-6 py-5 text-right">
                    <span className="font-mono-num text-burgundy font-medium text-base">{fmt(r.buy)}</span>
                  </div>
                  <div className="col-span-2 px-6 py-5 text-right">
                    <span className="font-mono-num text-coral font-semibold text-base">{fmt(r.sell)}</span>
                  </div>
                  <div className="col-span-3 px-6 py-5 text-right">
                    <span className="font-mono-num text-black/60 text-sm">{r.bnr_rate ? fmt(r.bnr_rate) : "—"}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-sm font-medium" data-testid="rates-note">
          {t.rates.negotiable.split("negociabile").length > 1 ? (
            <>
              {t.rates.negotiable.split("negociabile")[0]}
              <span className="text-coral font-semibold">negociabile</span>
              {t.rates.negotiable.split("negociabile")[1]}
            </>
          ) : t.rates.negotiable}
        </p>
      </div>
    </section>
  );
};

export default RatesTable;
